/**
 * The feature-flag registry as plain data (#304): no browser APIs and no build
 * globals, so Node scripts (the content digest, the catalog generator) can read
 * it as well as the app. `src/featureFlags.ts` resolves it for the app.
 *
 * `default`: `true` / `false`, or `"dev"` for ON in local dev (and the e2e dev
 * server) and OFF in the production build.
 */

/** A flag's default: on, off, or on in development only. */
export type FlagDefault = boolean | "dev";

export interface FeatureFlagDef {
  id: string;
  title: string;
  description: string;
  default: FlagDefault;
  /** Slugs of the `docs/features/` features this flag gates (#1204, #304).
   *  While the flag is off in production, those features aren't released:
   *  the content digest leaves them out of videos. When one of them is paid,
   *  it's a preview feature: the user's "Don't show me preview features"
   *  setting turns it off on top of this flag. */
  features?: string[];
}

/** Add a flag here, then gate its wiring with `isFeatureEnabled("id")`. */
export const FEATURE_FLAG_DEFS: ReadonlyArray<FeatureFlagDef> = [
  {
    id: "vector-export",
    features: ["optimized-vector-export"],
    title: "Optimized vector export",
    description:
      "Open export settings when exporting SVG / DXF (#572): content selection, laser cut / score / engrave colors, Cricut compatibility, and size-calibration boxes. When off, SVG / DXF export one-click with default settings.",
    default: true,
  },
  {
    id: "hardware-tool",
    features: ["hardware"],
    title: "Hardware tool",
    description: "The Hardware tool for placing rivets / snaps / hardware.",
    default: "dev",
  },
  {
    id: "stamping-section",
    features: [
      "single-stamp-placement",
      "bundled-stamp-library",
      "line-stamping",
      "tile-aware-fill",
      "custom-stamp-upload-local",
    ],
    title: "Stamping section",
    description:
      "The entire Stamping tool group (Stamp, Line stamp, and Fill).",
    default: "dev",
  },
  {
    id: "modifiers",
    features: ["magic-modifiers"],
    title: "Modifiers (point constraint, magic offset, magic mirror)",
    description:
      "Point-constraint snapping plus the live Offset and Mirror modifier sub-tools.",
    default: "dev",
  },
  {
    id: "construction-connections",
    features: ["construction-connections"],
    title: "Construction connections",
    description:
      "Live links from construction lines to the pieces / cutouts / stitch lines traced from them (their connection chips, and the cut-layout Spread/Assembled toggle). When off, traced geometry is baked with no live link.",
    // On by default — construction connections ship enabled; turn off to
    // get baked, un-linked traced geometry.
    default: true,
  },
  {
    id: "stitch-matching",
    features: ["connected-stitch-lines"],
    title: "Stitch matching",
    description:
      "Match two stitch lines so they share a hole count and line up hole-for-hole when sewn together, even across pieces of different length.",
    // On by default — stitch matching ships enabled; turn off to hide the
    // match UI and the construction-anchored stitch grid.
    default: true,
  },
  {
    id: "point-letters",
    title: "Point letters",
    description:
      "Letter the marked stitch holes where parts are sewn together, so the same hole is identifiable on both parts (and named in the assembly steps). When off, the holes are still marked — the letters naming them are not drawn.",
    // OFF by default — destined for Advanced Patterning, on the plans that
    // carry it. The chevrons that mark the shared holes are NOT gated by this;
    // only the letters that name them.
    default: false,
  },
  {
    id: "advanced-printing",
    features: ["advanced-printing"],
    title: "Advanced printing",
    description:
      "Print / PDF export open a settings dialog (orientation, paper size, margin, crop & registration marks) and drop the watermark. When off, the basic watermarked flow is used.",
    // On by default — advanced printing ships enabled; turn off to get the
    // basic watermarked flow.
    default: true,
  },
  {
    id: "account-buttons",
    title: "Account control in the top bar",
    description:
      'StartupAPI\'s account control at the end of the top bar (#1241): a "Log in" pill (Patreon) when signed out, and when signed in an avatar opening a menu with the profile, account settings and Log out. Off until the logged-in preview milestone is complete; login is optional, and the app works without it.',
    default: false,
  },
  {
    id: "image-server-storage",
    features: ["large-source-asset-storage"],
    title: "Server image storage",
    description:
      "Store traced photos — including the original — in your account on the server (#536). When off, or when not signed in, only the projected 200-DPI copy is kept (embedded inside the project); the original is discarded once that copy is ready, and nothing is sent to the server.",
    // On by default — server storage ships enabled for signed-in users, and
    // every early-access user is a patron (paid) today. A paid-tier feature
    // per the catalog (large-source-asset-storage); real tier entitlement
    // isn't wired up yet, so the flag is the release gate / kill switch.
    default: true,
  },
  {
    id: "layout",
    features: ["layout"],
    title: "Part auto-layout",
    description:
      "One-click Auto-layout of leather parts for printing — spreads overlapping parts apart. Advanced, paid-only. (Aligning / distributing parts by hand ships free.)",
    // Gates only the Auto-layout button now — align / distribute are free (#784).
    // A Maker-tier (paid) feature per the catalog. Enabled by default because
    // every early-access user effectively has Maker access today — payment
    // plans / real tier entitlement aren't wired up yet, so the flag itself is
    // the release gate. Turn off to hide the button.
    default: true,
  },
  {
    id: "advanced-stitch-control",
    features: ["stitch-layout-algorithms"],
    title: "Advanced stitch control",
    description:
      "The Advanced stitch control section on a seam (#691): even spacing with a pivot hole on every corner, an adjustable corner angle, round corner holes, and keeping the row's ends on pitch. When off, every seam marches at exact chisel pitch and the section is absent.",
    // Named for the panel section it governs, so the switch and the thing it
    // switches are findable from each other.
    //
    // A MAKER-AND-UP feature per the catalog
    // (docs/features/leather-pieces/stitch-layout-algorithms.md), gated exactly
    // as `layout` and the large-asset storage are: default ON because real tier
    // entitlement isn't wired into the client, and every early-access user
    // effectively has Maker access today. So this flag is the RELEASE gate, not
    // the entitlement check — the catalog's tier line is where the restriction
    // lives until that plumbing exists, and this default becomes an
    // entitlement lookup when it does. Turn it off and a seam that stored the
    // advanced layout still opens; it just lays out the ordinary way.
    default: true,
  },
  {
    id: "left-handed-layout",
    features: ["configurable-layout"],
    title: "Left-handed layout",
    description:
      "The Left-handed layout setting (#332): mirrors the workspace so the toolbars run down the right edge and the panels down the left. When off, the Settings row is absent and a stored preference is ignored — the workspace stays right-handed.",
    // Gates the NEW capability, not the whole Configurable layout feature it is
    // documented under: detach / dock already ships ungated, so a flag named
    // for the feature as a whole would switch off behaviour people have.
    //
    // A MAKER-AND-UP feature per the catalog
    // (docs/features/canvas-layout-customization/configurable-layout.md),
    // gated exactly as `layout` and `advanced-stitch-control`: default ON because
    // real tier entitlement isn't wired into the client and every early-access
    // user effectively has Maker access today. So this flag is the RELEASE
    // gate, not the entitlement check — the catalog's tier line is where the
    // restriction lives until that plumbing exists, and this default becomes an
    // entitlement lookup when it does.
    default: true,
  },
  {
    id: "shape-booleans",
    title: "Shape operations (boolean)",
    description:
      "Unite / Subtract / Intersect / Exclude / Divide on two or more closed shapes — construction lines, leather parts, or stitch loops (#764). When off, the Combine shapes bar is absent and shapes can only be combined by hand.",
    // On by default — a free-tier drawing operation per the catalog
    // (docs/features/drawing-construction/shape-operations.md). The flag is a
    // kill switch rather than an entitlement check: boolean geometry is the
    // one place a bad input can produce nonsense from an otherwise fine
    // project, so it needs to be switchable off without a redeploy.
    default: true,
  },
  {
    id: "angle-to-line-snap",
    title: "Angle snap to an existing line",
    description:
      "Angle snap also offers the drafting angles measured from the nearest construction line under the segment's start (#162), so a segment can be drawn parallel / perpendicular / at 45° to existing geometry and not only to the page. When off, angle snap uses the absolute table alone.",
    // On by default — it only ever ADDS candidates, and only within the same
    // reach the absolute table already has. The flag is a kill switch: this
    // changes the feel of a core drawing gesture, so it needs to be
    // switchable off without a redeploy.
    default: true,
  },
  {
    id: "touch-device-support",
    title: "Touch device support",
    description:
      "Drawing with a finger (#40): one finger draws where two fingers already pan and pinch, and handles grab wider for a fingertip than for a cursor. When off, a finger only ever moves the canvas, exactly as before.",
    // On by default since touch support was finished and tried on an iPad
    // (#1150, #1151). It changes what the primary gesture MEANS on a touch
    // device, so the flag stays as the kill switch: off, every touch contact
    // pans exactly as it did before.
    default: true,
  },
  {
    id: "theme-profiles",
    features: ["interface-theme-colors"],
    title: "Interface theme colors",
    description:
      "The Theme row in Settings, offering named interface palettes (#190) drawn from leatherworking color families. When off, the app uses the plain palette and the row is hidden. Never affects the leather, the cutting mat or the export layers.",
    // On by default. This is a RELEASE gate, not the entitlement check — the
    // feature is billed to Maker and up, but the tier plumbing doesn't exist
    // yet, so everyone gets it until it does and this flag is what turns it
    // off if the palettes cause trouble in the meantime.
    default: true,
  },
];

/** Whether a flag is on in the production build, before any override. */
export function onInProduction(def: FeatureFlagDef): boolean {
  return def.default === true;
}

/** Slugs of the features gated by a flag that's off in production: built,
 *  but not released yet. */
export function unreleasedFeatureSlugs(): Set<string> {
  const out = new Set<string>();
  for (const def of FEATURE_FLAG_DEFS) {
    if (onInProduction(def)) continue;
    for (const slug of def.features ?? []) out.add(slug);
  }
  return out;
}

/** A flag as a StartupAPI feature definition (#1244), the shape
 *  `createStartupAPI({ features })` takes. */
export interface StartupApiFeature {
  id: string;
  name: string;
  description: string;
  rolled_out_to_all_users: boolean;
}

/** The registry as StartupAPI's feature config (#1244). The build writes it
 *  to `dist/startupapi-features.json`, which the drawleather.com proxy syncs
 *  into its config. A flag that's on in production is rolled out to everyone;
 *  the rest (`false` and `"dev"`) stay off unless an admin enables them for a
 *  user or an account. */
export function startupApiFeatures(): StartupApiFeature[] {
  return FEATURE_FLAG_DEFS.map((def) => ({
    id: def.id,
    name: def.title,
    description: def.description,
    rolled_out_to_all_users: onInProduction(def),
  }));
}
